// src/containers/layout/oura-auth.tsx
//
// The merchant's shell. From 640px it is the same sidebar the staff business
// chrome uses (OuraSidebar): open or a rail from 1024px, always the rail from
// 640 to 1023px. Below 640px the same items run along a bottom bar, where a
// thumb reaches them: merchants open this from an email on the way somewhere
// (#3675), and a vertical rail on a 375px screen spends a fifth of the width
// before the page says anything.
//
// Staff never land here (OuraShell gives them the platform layout or the
// business chrome), but the shell still reads the role rather than assuming
// it, so the account menu and the home link stay right for whoever is in it.

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronsUpDownIcon, CompassIcon, KeyRoundIcon, LogOutIcon, SettingsIcon, UserIcon } from "lucide-react";
import { removeCookie } from "@util/cookie";
import { useUserProfileContext } from "@context/user_profile";
import { SnackbarProvider } from "@hook/use-snackbar";
import MaintenanceBanner from "@container/maintenance-banner";
import { ouraHref, ouraNavFor, OURA_ROOT, OURA_SETTINGS_PATH } from "@oura/nav";
import { useOuraRole, type OuraRole } from "@oura/role";
import { NotificationBell } from "@oura/components/NotificationBell";
import { ViewAsBanner, ViewAsRefusalNotice } from "@oura/components/ViewAsBanner";
import { getClientPage, invalidateClientPage, noticeLink, subscribeClientPage, type MyBusiness } from "@service/aeo/clientReview";
import { cn } from "@oura/lib/cn";
import { resetScanJobs } from "@oura/aeo";
import { badgeFor, useClientBadges } from "@oura/hooks/useClientBadges";
import { MERCHANT_V2 } from "@oura/merchantV2";
import ouraLogo from "@asset/oura_logo.png";
import ouraIcon from "@asset/oura_icon.png";
import { startMerchantTour } from "@oura/merchant/Tour";
import { SetupNeeded } from "@oura/merchant/SetupNeeded";
import { MERCHANT_TOPBAR_ID } from "@oura/components/ScanVersions";
import { FOCUS_RING, GLIDE, NAV, NoFold, OuraSidebar, PAGE_GROUND, useEscapeLayer, useGlideMark, type OuraSidebarItem, type OuraSidebarProps } from "@oura/ui";

function handleLogout() {
    removeCookie("authToken");
    removeCookie("__oura_dev_role__");
    // Clear the in-memory scan store before leaving. The navigation below is a
    // full page load, which already destroys it; this is here so that the day
    // logout becomes a client-side redirect, one user's scans cannot survive
    // into the next session on the same tab.
    resetScanJobs();
    window.location.href = "/";
}

// The OURA by ourteam logo (brand book: the platform shows Oura, ourteam in
// the lockup). The real files, never redrawn: the full logo open, the round
// icon closed. Both are 32px tall and start at the same x, so closing the rail
// leaves the circle exactly where it was and fades only the words.
//
// The box narrows to the icon when closed and clips the faded lockup, so the
// link and its focus ring stay inside the 64px rail (Jack, #4203).
function BrandTile({ collapsed }: { collapsed: boolean }) {
    return (
        <span
            className={cn(
                "relative block h-8 overflow-hidden transition-[width] duration-200 motion-reduce:transition-none",
                collapsed ? "w-8" : "w-[101px]",
            )}
        >
            <img src={ouraIcon} alt="" aria-hidden className="pointer-events-none absolute left-0 top-0 h-8 w-auto" />
            <img
                src={ouraLogo}
                alt="Oura by ourteam"
                className={cn(
                    "pointer-events-none absolute left-0 top-0 h-8 w-auto transition-opacity motion-reduce:transition-none",
                    collapsed ? "opacity-0 duration-100" : "opacity-100 duration-200 delay-100",
                )}
            />
        </span>
    );
}

/**
 * The merchant's own business, read off the client page.
 *
 * Four states, because a failed read is not "no business": `none` is only a
 * login the server confirmed holds none (404, or a staffer's 403), while a
 * lost connection or an expired session is an `error` the rail says out loud
 * and can retry. It re-reads whenever the client page is invalidated, so the
 * page's own Try again brings the rail back with it.
 */
type MyBusinessState =
    | { kind: "loading" }
    | { kind: "ready"; business: MyBusiness }
    | { kind: "none" }
    | { kind: "error" };

function useMyBusiness(enabled: boolean): { state: MyBusinessState; retry: () => void } {
    const [state, setState] = useState<MyBusinessState>(enabled ? { kind: "loading" } : { kind: "none" });
    useEffect(() => {
        if (!enabled) {
            setState({ kind: "none" });
            return;
        }
        // Back to the placeholder when the hook turns on, rather than
        // showing "Your business" while the first read is out.
        setState({ kind: "loading" });
        let cancelled = false;
        // An invalidation can start a read while an older one is still out,
        // and the older can land last. Only the newest read may set state.
        let latest = 0;
        const read = () => {
            const mine = ++latest;
            const current = () => !cancelled && mine === latest;
            return getClientPage().then(
                (r) => {
                    if (!current()) return;
                    if (r.ok) setState({ kind: "ready", business: r.payload.business });
                    else setState(r.reason === "notFound" || r.reason === "forbidden" ? { kind: "none" } : { kind: "error" });
                },
                () => {
                    if (current()) setState({ kind: "error" });
                },
            );
        };
        void read();
        const unsubscribe = subscribeClientPage(() => void read());
        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, [enabled]);
    // Past the shared read's cache, which keeps a failure for a few seconds;
    // the subscription above does the re-read.
    return { state, retry: invalidateClientPage };
}

/**
 * The account control: a profile-photo avatar opening Settings and Logout,
 * matching MinimalSidebarV2.
 *
 * It used to carry a dev-only "Preview as" role switch. That went when the
 * preview it returned from did: the merchant view is now reached by switching
 * into the merchant's workspace, which is a real session, so the way back is to
 * close the tab. Leaving the item would also have handed an actual merchant a
 * button labelled "Preview as staff", with nothing behind it they may see.
 *
 * The menu is portalled to the body and positioned `fixed` from the avatar's
 * own rectangle. The sidebar scrolls vertically, and a box that scrolls on
 * one axis clips on both, so a menu hung off it absolutely came out cut to
 * the rail's width; and the sidebar is sticky, which makes it its own
 * stacking context, so a menu left inside it painted under the page's cards.
 * `side` opens to the right of the sidebar; `up` opens above the phone bar.
 */
function UserMenu({ placement, isClient, collapsed = false }: { placement: "side" | "up"; isClient?: boolean; collapsed?: boolean }) {
    const [, userInfo] = useUserProfileContext();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const button = useRef<HTMLButtonElement>(null);
    const [pos, setPos] = useState<CSSProperties | null>(null);

    useLayoutEffect(() => {
        if (!open) {
            setPos(null);
            return;
        }
        const measure = () => {
            const b = button.current?.getBoundingClientRect();
            if (!b) return;
            const GAP = 8;
            setPos(
                placement === "side"
                    ? { left: b.right + GAP, bottom: Math.max(GAP, window.innerHeight - b.bottom) }
                    : { right: Math.max(GAP, window.innerWidth - b.right), bottom: window.innerHeight - b.top + GAP },
            );
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [open, placement]);

    const base = import.meta.env.VITE_ASSET_OURTEAM_BASE_URL;
    // A merchant's avatar is the person, not the business: the business's own
    // mark already heads the rail. No photo: their initials, not a grey silhouette.
    const avatarPath = isClient ? userInfo?.profile_picture_path : userInfo?.role !== "job_seeker" ? userInfo?.company_logo : userInfo?.profile_picture_path;
    const avatarSrc = avatarPath ? `${base}/${avatarPath}` : null;
    const avatarFallback = isClient ? null : `${base}/commons/${userInfo?.role === "admin" || userInfo?.role === "super_admin" ? "super-admin-logo.png" : "default_avatar.png"}`;
    const fullName = [userInfo?.first_name, userInfo?.last_name].filter((p) => p && p.trim()).join(" ").trim();
    const email = userInfo?.email ?? "";
    /** Open on the side rail: the whole row, so the account reads as who is signed in, not a stray picture. */
    const asRow = placement === "side" && !collapsed && !!isClient;

    /** Close, and put focus back on the avatar that opened the menu. */
    const close = () => {
        setOpen(false);
        button.current?.focus();
    };
    useEscapeLayer(close, open);

    const itemCls = "flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100";

    return (
        <div className={cn("relative", asRow && "w-full")}>
            {asRow ? (
                <button
                    ref={button}
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    aria-label="User menu"
                    aria-expanded={open}
                    className={cn(
                        "flex w-full items-center gap-2.5 rounded-[9px] py-1.5 pl-2 pr-2.5 text-left transition-colors hover:bg-gray-50 active:bg-gray-100 active:duration-0",
                        open && "bg-gray-50",
                        FOCUS_RING,
                    )}
                >
                    <Avatar src={avatarSrc} fallback={avatarFallback} name={fullName || email} size="sm" />
                    <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-gray-900">{fullName || email || "Your account"}</span>
                        {fullName && email && <span className="block truncate text-xs text-gray-500">{email}</span>}
                    </span>
                    <ChevronsUpDownIcon aria-hidden size={14} className="shrink-0 text-gray-400" />
                </button>
            ) : (
                <button
                    ref={button}
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    aria-label="User menu"
                    aria-expanded={open}
                    className={cn("flex items-center justify-center rounded-full", FOCUS_RING)}
                >
                    <Avatar src={avatarSrc} fallback={avatarFallback} name={fullName || email} size={placement === "side" ? "md" : "lg"} />
                </button>
            )}

            {open && pos && createPortal(
                <>
                    {/* click-away layer */}
                    <div aria-hidden className="fixed inset-0 z-30" onClick={close} />
                    <div style={pos} className={cn("fixed z-40 rounded-md border border-gray-200 bg-white py-1 shadow-lg", isClient ? "w-56" : "w-44")}>
                        {/* Who is signed in, where the rail cannot say it (collapsed, or the phone bar). */}
                        {isClient && !asRow && (fullName || email) && (
                            <div className="mb-1 border-b border-gray-100 px-4 pb-2 pt-1.5">
                                <p className="m-0 truncate text-sm font-medium text-gray-900">{fullName || email}</p>
                                {fullName && email && <p className="m-0 truncate text-xs text-gray-500">{email}</p>}
                            </div>
                        )}
                        {/* Above Settings, because it is the thing a new
                            owner wants and Settings is not (#4062 MR-FE-10).
                            Clients only: the tour is about their pages. */}
                        {MERCHANT_V2 && isClient && (
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    startMerchantTour();
                                }}
                                className={itemCls}
                            >
                                <CompassIcon className="mr-2 h-4 w-4" />
                                How this works
                            </button>
                        )}
                        {/* Setup left the rail (five items): it is a tab on
                            To do, and this opens To do on it. */}
                        {MERCHANT_V2 && isClient && (
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    navigate(`${ouraHref("decisions", { role: "oura-client" })}?tab=setup`);
                                }}
                                className={itemCls}
                            >
                                <KeyRoundIcon className="mr-2 h-4 w-4" />
                                Setup
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => {
                                setOpen(false);
                                navigate(OURA_SETTINGS_PATH);
                            }}
                            className={itemCls}
                        >
                            <SettingsIcon className="mr-2 h-4 w-4" />
                            Settings
                        </button>
                        <button type="button" onClick={handleLogout} className={itemCls}>
                            <LogOutIcon className="mr-2 h-4 w-4" />
                            Logout
                        </button>
                    </div>
                </>,
                document.body,
            )}
        </div>
    );
}

const AVATAR_SIZE = { sm: "h-8 w-8 text-xs", md: "h-9 w-9 text-xs", lg: "h-11 w-11 text-sm" } as const;

/** Initials from a name ("Jovi Chu" -> "JC") or, with no name, the email's first letter. */
function initialsOf(name: string): string {
    const words = name.includes("@") ? [name] : name.split(/\s+/).filter(Boolean);
    return words.map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

/**
 * The signed-in person's picture. No picture, or one that fails to load: the
 * fallback image when there is one (staff), else their initials on blue.
 */
function Avatar({ src, fallback, name, size }: { src: string | null; fallback: string | null; name: string; size: keyof typeof AVATAR_SIZE }) {
    const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());
    const shown = [src, fallback].find((u): u is string => !!u && !failed.has(u)) ?? null;
    const initials = initialsOf(name);
    return (
        <span className={cn("grid shrink-0 place-items-center overflow-hidden rounded-full", AVATAR_SIZE[size], shown ? "border border-gray-200 bg-white" : "bg-blue-100 font-semibold text-blue-700")}>
            {shown ? (
                <img alt="" src={shown} className="h-full w-full object-cover" onError={() => setFailed((was) => new Set(was).add(shown))} />
            ) : initials ? (
                initials
            ) : (
                <UserIcon aria-hidden size={16} />
            )}
        </span>
    );
}

/**
 * The notification centre, scoped to the merchant's own business (from the
 * login, not the URL). A notice that names where it opens (a shared document,
 * a reply to a request) goes there; a notice about one ask opens that ask on
 * Decisions; any other notice (a plan update, a report) opens Home, which is
 * where the merchant reads what changed.
 */
function ClientBell({ businessId, placement }: { businessId: string; placement: "sideTop" | "up" }) {
    const navigate = useNavigate();
    return (
        <NotificationBell
            audience="client"
            businessId={businessId}
            rail={placement}
            onOpen={(n) => {
                const link = noticeLink(n);
                // The notice goes along, so the page it opens can quote it:
                // Work highlights the request in Your requests, and quotes
                // the notice only when that list cannot show it.
                if (link) navigate(link, { state: { notice: { title: n.title, body: n.body } } });
                else navigate(n.itemId ? `${OURA_ROOT}/decisions?item=${encodeURIComponent(n.itemId)}` : `${OURA_ROOT}/dashboard`);
            }}
        />
    );
}

/** Shorter words on the phone's bottom bar, where seven targets share 375px; the full name stays the accessible one. */
// Seven tabs share 375px, so the long names take a short word on the bar.
const BAR_LABEL: Record<string, string> = { Decisions: "Decide", "Our work": "Work", Website: "Site" };

/** One item in the phone bottom bar: the sidebar's icon, tint and badge, with the label under it. */
function BarItem({ item }: { item: OuraSidebarItem }) {
    const Icon = item.icon;
    const badge = item.badge && item.badge.count > 0 ? item.badge : null;
    const short = BAR_LABEL[item.label] ?? null;
    return (
        <Link
            to={item.to ?? OURA_ROOT}
            // The tour points at the bar on a phone, the rail on a desktop.
            data-nav-view={item.key}
            aria-current={item.active ? "page" : undefined}
            aria-label={badge ? `${item.label}, ${badge.label}` : short ? item.label : undefined}
            className={cn("group flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-lg py-1", FOCUS_RING)}
        >
            <span data-bar-pill className={cn("relative rounded-lg p-1.5 transition-colors", item.active ? NAV.barIconActive : NAV.barIconRest)}>
                <Icon aria-hidden size={20} />
                {badge && (
                    <span
                        aria-hidden
                        className="absolute -right-1 -top-1 h-4 min-w-4 rounded-full bg-amber-50 px-1 text-center text-xs font-semibold leading-4 tabular-nums text-amber-800 ring-1 ring-amber-200"
                    >
                        {badge.count}
                    </span>
                )}
            </span>
            {/* Tight tracking: seven targets share 375px; a long name takes its short word here.
                Never cut: a word a pixel or two wider than its slot runs into
                the gap beside it, which is wider than that, rather than
                ending in "…". */}
            <span className={cn("whitespace-nowrap text-xs tracking-tight", item.active ? NAV.barLabelActive : "text-gray-500")}>{short ?? item.label}</span>
        </Link>
    );
}

interface Props {
    children: ReactNode;
}

export default function OuraAuthLayout({ children }: Props) {
    const role = useOuraRole();
    const { pathname } = useLocation();
    const isClient = role === "oura-client";
    const { state: businessState, retry: retryBusiness } = useMyBusiness(isClient);
    const { asks: openAsks, asksExact, unreadReports, unreadDocuments, term, setupMine } = useClientBadges(isClient);

    // The redesign's shell is the merchant's only. Oura staff reach this
    // layout on their list, Requests, Skills, Tracker and Settings, with the
    // same rail and the page in its own padding.
    const merchantShell = MERCHANT_V2 && isClient;

    // Compare against the path only: the Settings item carries a ?product hint.
    const isActive = (to: string) => pathname === to.split("?")[0];
    const items: OuraSidebarItem[] = ouraNavFor(role)
        .flatMap((group) => group.items)
        .map((item) => ({
            key: item.view,
            label: item.label,
            icon: item.icon,
            active: isActive(item.to) || (item.alsoActive ?? []).some(isActive),
            to: item.to,
            // What the page is for, on hover and on keyboard focus (C-FE-5).
            // Absent on the staff rail, which shows no tooltip there.
            hint: item.hint,
            badge: badgeFor(item.view, openAsks, unreadReports, asksExact, setupMine, unreadDocuments),
        }));
    const businessId = businessState.kind === "ready" ? businessState.business.id : null;
    const barRef = useRef<HTMLElement>(null);
    const barActive = items.find((it) => it.active)?.key ?? null;
    const { mark: barMark, markRef: barMarkRef } = useGlideMark(barRef, barActive ? `[data-nav-view="${CSS.escape(barActive)}"] [data-bar-pill]` : null, "oura.phoneBar", items.length);

    return (
        <SnackbarProvider>
            {/* The redesign: from 640px the page is the height of the window
                and only the content column scrolls, so the rail never moves. */}
            <div className={cn("min-h-screen sm:flex sm:items-stretch", merchantShell && "sm:h-screen sm:overflow-hidden", PAGE_GROUND)}>
                <OuraSidebar
                    ariaLabel="Oura"
                    storageKey="oura.merchantSidebar.collapsed"
                    phoneHidden
                    // The bell sits with the logo: at its right while the
                    // rail is open, under it while the rail is collapsed.
                    brand={(collapsed) => (
                        // Collapsed, `-ml-2 w-12` cancels the slot's left
                        // padding so both centre on the rail's middle, where
                        // every nav icon below sits.
                        <div className={cn("flex", collapsed ? "-ml-2 w-12 flex-col items-center gap-3" : "w-full items-center justify-between pr-1")}>
                            <Link to={ouraHome(role)} className={cn("rounded", FOCUS_RING)}>
                                <BrandTile collapsed={collapsed} />
                            </Link>
                            {businessId && <ClientBell businessId={businessId} placement="sideTop" />}
                        </div>
                    )}
                    identity={identityFor(businessState, retryBusiness, term, !isClient)}
                    groups={[{ key: "merchant", items }]}
                    // Open: the account at the left edge. Collapsed: on the
                    // rail's middle, under the nav icons. The bell moved up
                    // beside the logo.
                    foot={(collapsed) => (
                        <div className={cn("flex", collapsed && "justify-center")}>
                            <UserMenu placement="side" isClient={isClient} collapsed={collapsed} />
                        </div>
                    )}
                />

                {/* Below 640px: the same items along the bottom, then the bell and the account. */}
                <nav
                    ref={barRef}
                    aria-label="Oura"
                    className="fixed inset-x-0 bottom-0 z-20 flex h-16 items-center border-t border-gray-200 bg-white px-1 sm:hidden"
                >
                    {/* The wash under the page you are on, gliding from tab to tab (glide.ts). */}
                    {barMark && (
                        <span
                            ref={barMarkRef}
                            aria-hidden
                            className={cn(GLIDE, "rounded-lg", NAV.activeWash)}
                            style={{ width: barMark.w, height: barMark.h, transform: `translate(${barMark.x}px, ${barMark.y}px)` }}
                        />
                    )}
                    {items.map((item) => (
                        <BarItem key={item.key} item={item} />
                    ))}
                    {businessId && (
                        <div className="shrink-0">
                            <ClientBell businessId={businessId} placement="up" />
                        </div>
                    )}
                    <div className="shrink-0 pr-1">
                        <UserMenu placement="up" isClient={isClient} />
                    </div>
                </nav>

                {/* Bottom padding on a phone so the bar never covers the last row. */}
                {/* The redesign: a white page for the merchant (Jovi, 23 Oct:
                    the merchant's screens stay white; staff keep the soft grey
                    ground), a hairline between it and the white rail, content
                    at 90% of the column from 1024px, and no card folds (an
                    owner's screens hold too little for folding to save
                    anything). */}
                <main
                    className={cn(
                        "min-w-0 flex-1",
                        merchantShell && "bg-white sm:overflow-y-auto sm:border-l sm:border-gray-200",
                    )}
                >
                    {/* Edge to edge across the content column, above the page's
                        own padding: maintenance reaches Oura staff and merchants
                        as it does every other customer (#3561). */}
                    <MaintenanceBanner />
                    {/* Edge to edge across the content column and pinned to
                        its top: the bar a past scan portals into (PastScanBar).
                        Hidden while empty. */}
                    {merchantShell && <div id={MERCHANT_TOPBAR_ID} className="sticky top-0 z-20 empty:hidden" />}
                    <div className="px-4 pb-24 pt-7 sm:pb-10 md:px-8">
                        <div className={merchantShell ? "mx-auto lg:max-w-[90%]" : "mx-auto max-w-6xl"}>
                            {/* Staff looking at the merchant's screens (#4112); nothing for a real merchant. */}
                            <ViewAsBanner businessName={businessState.kind === "ready" ? businessState.business.name : null} />
                            {/* Parts of our work wait on their setup: said on every page until it is done. */}
                            {merchantShell && <SetupNeeded />}
                            {merchantShell ? <NoFold>{children}</NoFold> : children}
                        </div>
                    </div>
                </main>
                <ViewAsRefusalNotice />
            </div>
        </SnackbarProvider>
    );
}

/**
 * The rail's name line: a placeholder bar while loading, "Your business" for a
 * login with none to read (a staffer previewing), and an explicit failure with
 * a retry, never a quiet "Your business", when the read itself failed.
 */
function identityFor(
    state: MyBusinessState,
    retry: () => void,
    term: { month: number; of: number } | null,
    staff = false,
): OuraSidebarProps["identity"] {
    // Where they are in the term, under their own name (#4062 MR-FE-2).
    // "Your Oura" said nothing they did not already know from being here;
    // "Month 3 of 12" is the one fact that answers "how far along are we".
    // Only when the payload carries both, so it is never a guess.
    const sub = MERCHANT_V2 && term ? `Month ${term.month} of ${term.of}` : "Your Oura";
    switch (state.kind) {
        case "loading":
            return { name: null, sub };
        case "ready":
            return { name: state.business.name, sub, icon: state.business.faviconUrl ?? null };
        case "none":
            // A staff login reads no business of its own: the rail says whose workspace it is.
            return staff ? { name: "ourteam", sub: "Staff" } : { name: "Your business", sub };
        case "error":
            return {
                name: "Your business",
                sub: <span className="text-rose-700">Could not load it</span>,
                action: (
                    <button type="button" onClick={retry} className={cn("shrink-0 text-xs font-medium text-blue-600 hover:underline", FOCUS_RING)}>
                        Retry
                    </button>
                ),
            };
    }
}

/** A role's landing route: staff to the list, the merchant to Home (still /oura/dashboard, D1). */
function ouraHome(role: OuraRole): string {
    return role === "oura-staff" ? `${OURA_ROOT}/businesses` : `${OURA_ROOT}/dashboard`;
}
